import { WS } from '../ws/WS';
import { Track, TRACK_ID } from '@hugendubel/tracking_manager';
import { LoadingScreen } from '@hugendubel/react-basic';
import { WsInitResult } from '../ws/result/WsInitResult';
import { WsClient, WsErrorCode, TokenStore } from '@hugendubel/ws_client';
import { AuthService } from '@hugendubel/login';
import { Store } from '../store';
import { BonusPoints } from '../ui/BonusPoints';
import { Config } from '../config';
import { Message } from 'first-intl';
import { Globals } from '../globals';

type InitResponse = { participates?: boolean; error?: Message; tokenWasCleared?: boolean };

export const _InitService = {
    init: (uuid?: string): Promise<InitResponse> => {
        // first display a loading screen as long as this critical calls are running
        LoadingScreen.open('loading.init');
        // we need to remove the previous tokens if the application gets opened with present uuid
        // this means the uuid is present and the Config was never initialized
        // ATTENTION: We remove the token "silently" by calling "TokenStore.remove" instead of
        //            "WsClient.Controller.update(undefined)" because this would introduce various site effects
        //            e. g. in "AuthService.onWsClientUpdate" and "LoginMask"
        uuid && !Config.initDone() && TokenStore.remove();
        // and finally make the call to initialize the bonus shop
        const url = Globals.window().location.href;
        return WS.init({ uuid, url })
            .then(({ tokenPair, points, mediaBaseUrl, isPhysicalBookstore, customerData, backLink }: WsInitResult) => {
                Config.init({ mediaBaseUrl, isPhysicalBookstore, customerData });
                Store.set({ points: new BonusPoints(points), backLink });
                if (tokenPair) WsClient.Controller.update(tokenPair);
                return { participates: true };
            })
            .catch(error => {
                switch ((error as any).errorCode || 0) {
                    case WsErrorCode.NO_VALID_TOKEN:
                    case WsErrorCode.EXPIRED_TOKEN:
                        return { tokenWasCleared: true };
                    case WsErrorCode.LOYALTY_CUSTOMER_UNCONNECTED:
                        if (uuid) {
                            // we need to logout if the try failed with uuid and
                            // display that this should be a not expected state
                            Track.error(
                                TRACK_ID('2019-09-09 01:33:58'),
                                'Corresponding customer for UUID was unconnected.'
                            );
                            AuthService.logout();
                            return { error: 'page.NotAvailable.unexpected' };
                        }
                        // in this case we have to propagate the error to show the ParticipationRequired page
                        return { participates: false };
                    case WsErrorCode.LOYALTY_BACKEND_EXCEPTION:
                        Track.error(TRACK_ID('2019-09-09 10:30:04'), 'Encountered loyalty backend exception.');
                        return { error: 'page.NotAvailable.loyaltyBackend' };
                    case WsErrorCode.LOYALTY_CUSTOMER_UNKNOWN:
                        Track.error(TRACK_ID('2019-09-09 10:30:24'), 'Loyalty customer was unknown to the backend.');
                        return { error: 'page.NotAvailable.loyaltyNoCustomer' };
                    default:
                        Track.error(TRACK_ID('2019-09-09 10:30:46'), `Ws.init returned an error: ${error.message}`);
                        return { error: 'page.NotAvailable.unexpected' };
                }
            })
            .then(r => {
                LoadingScreen.close();
                return r;
            });
    },
};

const initWithUuid = (uuid: string) => _InitService.init(uuid);
const initWithToken = () => _InitService.init();

export const InitService = { initWithUuid, initWithToken };
